import {
  FACT_KEY_NEEDS,
  FACT_KEY_PRODUCTS,
  NEED_SITUATIONS,
  PROPERTY_CATEGORIES,
  PROPERTY_SUBTYPES,
  TRANSACTION_TYPES,
  type FactKeyNeed,
  type FactKeyNeedCell,
  type FactKeyProduct,
  type NeedSituation,
  type PropertyCategory,
  type PropertySubtype,
  type TransactionType,
} from '@immo/api-contract';

/** Strongest first; `NOT_USED` is last, so a product's strongest level never picks it over a used one. */
export const NEED_STRENGTH = FACT_KEY_NEEDS;

export type NeedFilterNeed = FactKeyNeed;

export const NEED_FILTER_VALUES: ReadonlyArray<string> = FACT_KEY_PRODUCTS.flatMap((product) =>
  FACT_KEY_NEEDS.map((need) => `${product}.${need}`),
);

export type ProductNeed = { readonly product: FactKeyProduct; readonly need: FactKeyNeed };

type Conditions = {
  readonly marketingType: TransactionType | null;
  readonly subtype: PropertySubtype | null;
  readonly situation: NeedSituation | null;
};

/** A need row being edited; `id` is client-only and never sent. */
export type DraftNeedRow = FactKeyNeedCell & { readonly id: number };

let lastRowId = 0;

export const nextNeedRowId = (): number => ++lastRowId;

export const toDraftRows = (needs: ReadonlyArray<FactKeyNeedCell>): ReadonlyArray<DraftNeedRow> =>
  needs.map((cell) => ({ ...cell, id: nextNeedRowId() }));

export const withoutId = ({ id: _id, ...cell }: DraftNeedRow): FactKeyNeedCell => cell;

const conditionCount = (conditions: Conditions): number =>
  [conditions.marketingType, conditions.subtype, conditions.situation].filter((value) => value !== null).length;

const position = <T extends string>(order: ReadonlyArray<T>, value: T | null): number =>
  value === null ? -1 : order.indexOf(value);

/** Display order: product, object type, number of conditions, then marketing type, subtype, situation (null first). */
export function compareNeedRows(a: FactKeyNeedCell, b: FactKeyNeedCell): number {
  return (
    FACT_KEY_PRODUCTS.indexOf(a.product) - FACT_KEY_PRODUCTS.indexOf(b.product) ||
    PROPERTY_CATEGORIES.indexOf(a.category) - PROPERTY_CATEGORIES.indexOf(b.category) ||
    conditionCount(a) - conditionCount(b) ||
    position(TRANSACTION_TYPES, a.marketingType) - position(TRANSACTION_TYPES, b.marketingType) ||
    position(PROPERTY_SUBTYPES, a.subtype) - position(PROPERTY_SUBTYPES, b.subtype) ||
    position(NEED_SITUATIONS, a.situation) - position(NEED_SITUATIONS, b.situation)
  );
}

export const sortNeedRows = <T extends FactKeyNeedCell>(rows: ReadonlyArray<T>): T[] => [...rows].sort(compareNeedRows);

const identityOf = (row: FactKeyNeedCell): string =>
  [row.product, row.category, row.marketingType ?? '', row.subtype ?? '', row.situation ?? ''].join('|');

const exactlyOf = (row: FactKeyNeedCell): string => `${identityOf(row)}|${row.need}`;

const isUnconditionedNotUsed = (row: FactKeyNeedCell): boolean => row.need === 'NOT_USED' && conditionCount(row) === 0;

/** What the server stores: no unconditioned „Nicht genutzt“, no exact duplicates, in display order. */
export function normaliseNeedRows(rows: ReadonlyArray<FactKeyNeedCell>): FactKeyNeedCell[] {
  const seen = new Set<string>();

  return sortNeedRows(rows).filter((row) => {
    if (isUnconditionedNotUsed(row) || seen.has(exactlyOf(row))) return false;

    seen.add(exactlyOf(row));

    return true;
  });
}

/** Row-by-row equality of the normalised rows. */
export function sameNeeds(a: ReadonlyArray<FactKeyNeedCell>, b: ReadonlyArray<FactKeyNeedCell>): boolean {
  const left = normaliseNeedRows(a).map(exactlyOf);
  const right = normaliseNeedRows(b).map(exactlyOf);

  return left.length === right.length && left.every((row, index) => row === right[index]);
}

/** One entry per product that has a used row, holding its strongest need; strongest first, then product order. */
export function strongestNeedByProduct(needs: ReadonlyArray<FactKeyNeedCell>): ReadonlyArray<ProductNeed> {
  const strongest = new Map<FactKeyProduct, FactKeyNeed>();

  for (const cell of needs) {
    if (cell.need === 'NOT_USED') continue;

    const current = strongest.get(cell.product);

    if (current === undefined || NEED_STRENGTH.indexOf(cell.need) < NEED_STRENGTH.indexOf(current))
      strongest.set(cell.product, cell.need);
  }

  return [...strongest.entries()]
    .map(([product, need]) => ({ product, need }))
    .sort(
      (a, b) =>
        NEED_STRENGTH.indexOf(a.need) - NEED_STRENGTH.indexOf(b.need) ||
        FACT_KEY_PRODUCTS.indexOf(a.product) - FACT_KEY_PRODUCTS.indexOf(b.product),
    );
}

/** The index of the strongest need over all products, `null` when the entry has no used row. */
export function needSortValue(needs: ReadonlyArray<FactKeyNeedCell>): number | null {
  const [first] = strongestNeedByProduct(needs);

  return first === undefined ? null : NEED_STRENGTH.indexOf(first.need);
}

/** „Nicht genutzt“ matches an entry with no used row for the product. */
export function matchesNeedFilter(needs: ReadonlyArray<FactKeyNeedCell>, value: string): boolean {
  const [product, need] = value.split('.');

  if (need === 'NOT_USED') return !needs.some((cell) => cell.product === product && cell.need !== 'NOT_USED');

  return needs.some((cell) => cell.product === product && cell.need === need);
}

export type ConditionLabels = {
  readonly marketingType: (value: TransactionType) => string;
  readonly subtype: (value: PropertySubtype) => string;
  readonly situation: (value: NeedSituation) => string;
};

/** The set conditions of a row joined by „ · “, e.g. „Kauf · Mehrfamilienhaus“; empty when it has none. */
export function conditionsLabel(row: Conditions, labels: ConditionLabels): string {
  return [
    row.marketingType === null ? null : labels.marketingType(row.marketingType),
    row.subtype === null ? null : labels.subtype(row.subtype),
    row.situation === null ? null : labels.situation(row.situation),
  ]
    .filter((part) => part !== null)
    .join(' · ');
}

export type NeedConflict = { readonly offendingId: number; readonly partner: DraftNeedRow };

export type AmbiguousDetails = {
  readonly product: FactKeyProduct;
  readonly category: PropertyCategory;
  readonly rows: ReadonlyArray<Conditions & { readonly need: FactKeyNeed }>;
};

/**
 * Maps the server's refused pair onto the draft rows. The offending row is the one the operator just
 * changed — the pair member not identical to a stored row; when both or neither are, the second.
 */
export function conflictOf(
  details: AmbiguousDetails,
  draftRows: ReadonlyArray<DraftNeedRow>,
  storedRows: ReadonlyArray<FactKeyNeedCell>,
): NeedConflict | null {
  const [first, second] = details.rows.map((row) =>
    draftRows.find(
      (draft) =>
        draft.product === details.product &&
        draft.category === details.category &&
        exactlyOf(draft) === exactlyOf({ ...row, product: details.product, category: details.category }),
    ),
  );

  if (first === undefined || second === undefined) return null;

  const stored = new Set(storedRows.map(exactlyOf));
  const firstIsStored = stored.has(exactlyOf(first));
  const secondIsStored = stored.has(exactlyOf(second));

  return firstIsStored !== secondIsStored
    ? firstIsStored
      ? { offendingId: second.id, partner: first }
      : { offendingId: first.id, partner: second }
    : { offendingId: second.id, partner: first };
}

/** Two rows with the same identity and different levels: the boundary would refuse them without details. */
export function duplicateOf(rows: ReadonlyArray<DraftNeedRow>): NeedConflict | null {
  const sorted = sortNeedRows(rows);

  for (const [index, row] of sorted.entries()) {
    const partner = sorted
      .slice(0, index)
      .find((other) => identityOf(other) === identityOf(row) && other.need !== row.need);

    if (partner !== undefined) return { offendingId: row.id, partner };
  }

  return null;
}
